import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
	UserCircleIcon, 
	PencilIcon, 
	CheckIcon, 
	XMarkIcon,
	MagnifyingGlassIcon,
	FunnelIcon,
	PlusIcon,
	TrashIcon
} from '@heroicons/react/24/outline';
import { maskPhone, maskCedula, maskRNC, unmask } from '../../utils/masks';
import ActividadEconomicaSelector from '../common/ActividadEconomicaSelector';
import { dgiiApi } from '../../api/dgii';

import { API_URL } from '../../config/api';

const UserManagement = () => {
	const { user: currentUser, isAdmin, isContador, isAdminOrContador, loadUser } = useAuth();
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [noUsersYet, setNoUsersYet] = useState(false);
	const [showActividadModal, setShowActividadModal] = useState(false);
	const [nuevaActividadData, setNuevaActividadData] = useState({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
	const [searchTerm, setSearchTerm] = useState('');
	const [filterRole, setFilterRole] = useState('all');
	const [filterActive, setFilterActive] = useState('all');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingUser, setEditingUser] = useState(null);
	const [formData, setFormData] = useState({
		username: '',
		email: '',
		password: '',
		password_confirm: '',
		first_name: '',
		last_name: '',
		telefono: '',
		cedula: '',
		rnc_empresa: '',
		empresa: '',
		actividad_economica: '',
		role: 'cliente',
		is_active: true
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);

	// Roles que puede asignar este usuario al crear/editar
	const rolesDisponibles = isAdmin()
		? [
				{ value: 'cliente', label: 'Cliente' },
				{ value: 'gestor', label: 'Gestor' },
				{ value: 'contador', label: 'Contador' },
				{ value: 'admin', label: 'Administrador' },
			]
		: [
				// Contador solo puede crear clientes y gestores
				{ value: 'cliente', label: 'Cliente' },
				{ value: 'gestor', label: 'Gestor' },
			];

	useEffect(() => {
		if (isAdminOrContador()) {
			loadUsers();
		}
	}, []);

	const loadUsers = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/auth/users/`);
			// Manejar respuesta paginada o array directo
		const usersData = Array.isArray(response.data) 
			? response.data 
			: (response.data.results || []);
		setUsers(usersData);
		setError('');
		// Si es Contador y solo se devuelve él mismo (0 usuarios creados), mostrar aviso
		setNoUsersYet(false);
	} catch (err) {
		console.error('Error cargando usuarios:', err);
		setUsers([]);
		// Si es Contador y aún no tiene usuarios creados, mostrar aviso amigable
		if (err.response?.status === 500 || err.response?.status === 404) {
			setNoUsersYet(true);
			setError('');
		} else {
			setError('Error al cargar usuarios');
		}
	} finally {
		setLoading(false);
	}
	};

	const handleCreate = () => {
		setFormData({
			username: '',
			email: '',
			password: '',
			password_confirm: '',
			first_name: '',
			last_name: '',
			telefono: '',
			cedula: '',
			rnc_empresa: '',
			empresa: '',
			actividad_economica: '',
			role: 'cliente',
			is_active: true
		});
		setFormErrors({});
		setEditingUser(null);
		setShowCreateModal(true);
	};

	const handleEdit = (user) => {
		setFormData({
			username: user.username,
			email: user.email,
			password: '',
			password_confirm: '',
			first_name: user.first_name || '',
			last_name: user.last_name || '',
			telefono: user.profile?.telefono ? maskPhone(user.profile.telefono) : '',
			cedula: user.profile?.cedula ? maskCedula(user.profile.cedula) : '',
			// rnc_empresa sin máscara, solo números
			rnc_empresa: user.profile?.rnc_empresa ? user.profile.rnc_empresa.replace(/\D/g, '') : '',
			empresa: user.profile?.empresa || '',
			actividad_economica: user.profile?.actividad_economica || '',
			role: user.profile?.role || 'cliente',
			is_active: user.is_active
		});
		setFormErrors({});
		setEditingUser(user);
		setShowEditModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			if (editingUser) {
				// Actualizar usuario existente
				const updateData = {
					username: formData.username,
					email: formData.email,
					first_name: formData.first_name,
					last_name: formData.last_name,
					is_active: formData.is_active
				};

				// Solo incluir password si se proporcionó
				if (formData.password) {
					if (formData.password !== formData.password_confirm) {
						setFormErrors({ password_confirm: 'Las contraseñas no coinciden' });
						setSaving(false);
						return;
					}
					updateData.password = formData.password;
				}

				// Incluir datos del perfil en la actualización
				updateData.profile = {
					telefono: unmask(formData.telefono),
					cedula: unmask(formData.cedula),
					// rnc_empresa ya no tiene máscara, solo números
					rnc_empresa: formData.rnc_empresa,
					empresa: formData.empresa,
					actividad_economica: formData.actividad_economica,
					role: formData.role
				};

				await axios.put(`${API_URL}/api/auth/users/${editingUser.id}/`, updateData);
			} else {
				// Crear nuevo usuario
				if (formData.password !== formData.password_confirm) {
					setFormErrors({ password_confirm: 'Las contraseñas no coinciden' });
					setSaving(false);
					return;
				}

				await axios.post(`${API_URL}/api/auth/register/`, {
					username: formData.username,
					email: formData.email,
					password: formData.password,
					password_confirm: formData.password_confirm,
					first_name: formData.first_name,
					last_name: formData.last_name,
					telefono: unmask(formData.telefono),
					cedula: unmask(formData.cedula),
					// rnc_empresa ya no tiene máscara, solo números
					rnc_empresa: formData.rnc_empresa,
					empresa: formData.empresa,
					actividad_economica: formData.actividad_economica,
					role: formData.role
				});
			}

			await loadUsers();
			setShowCreateModal(false);
			setShowEditModal(false);
			setEditingUser(null);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar usuario');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleToggleActive = async (userId) => {
		try {
			await axios.post(`${API_URL}/api/auth/users/${userId}/toggle_active/`);
			await loadUsers();
		} catch (err) {
			setError('Error al cambiar estado del usuario');
		}
	};

	const getRoleBadgeColor = (role) => {
		switch (role) {
			case 'admin':
				return 'bg-red-100 text-red-800';
			case 'gestor':
				return 'bg-blue-100 text-blue-800';
			case 'contador':
				return 'bg-purple-100 text-purple-800';
			case 'cliente':
				return 'bg-green-100 text-green-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	const getRoleLabel = (role) => {
		switch (role) {
			case 'admin':
				return 'Administrador';
			case 'gestor':
				return 'Gestor';
			case 'contador':
				return 'Contador';
			case 'cliente':
				return 'Cliente';
			default:
				return role;
		}
	};

	const filteredUsers = (Array.isArray(users) ? users : []).filter(user => {
		const matchesSearch = 
			user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.last_name?.toLowerCase().includes(searchTerm.toLowerCase());

		const matchesRole = filterRole === 'all' || user.profile?.role === filterRole;
		const matchesActive = 
			filterActive === 'all' || 
			(filterActive === 'active' && user.is_active && user.profile?.activo) ||
			(filterActive === 'inactive' && (!user.is_active || !user.profile?.activo));

		return matchesSearch && matchesRole && matchesActive;
	});

	if (!isAdminOrContador()) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-gray-900">Acceso Denegado</h2>
					<p className="mt-2 text-gray-600">Solo los administradores y contadores pueden acceder a esta página.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
							<p className="mt-2 text-sm text-gray-600">
								{isAdmin()
									? 'Administra usuarios, roles y permisos del sistema'
									: 'Gestiona los usuarios que has creado y sus accesos'}
							</p>
						</div>
						<button
							onClick={handleCreate}
							className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
						>
							<PlusIcon className="h-5 w-5" />
							Crear Usuario
						</button>
					</div>

				</div>

				{/* Filtros y búsqueda */}
				<div className="bg-white rounded-lg shadow p-4 mb-6">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						{/* Búsqueda */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Buscar
							</label>
							<div className="relative">
								<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									type="text"
									placeholder="Buscar usuarios..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								/>
							</div>
						</div>

						{/* Filtro por rol */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Rol
							</label>
							<select
								value={filterRole}
								onChange={(e) => setFilterRole(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
							>
								<option value="all">Todos</option>
								{isAdmin() && <option value="admin">Administrador</option>}
								{isAdmin() && <option value="contador">Contador</option>}
								<option value="gestor">Gestor</option>
								<option value="cliente">Cliente</option>
							</select>
						</div>

						{/* Filtro por estado */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Estado
							</label>
							<select
								value={filterActive}
								onChange={(e) => setFilterActive(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
							>
								<option value="all">Todos</option>
								<option value="active">Activos</option>
								<option value="inactive">Inactivos</option>
							</select>
						</div>
					</div>
				</div>

				{/* Tabla de usuarios */}
				{loading ? (
					<div className="text-center py-12">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
						<p className="mt-4 text-gray-600">Cargando usuarios...</p>
					</div>
			) : noUsersYet ? (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-4">
					<div className="flex-shrink-0 text-amber-500 text-2xl">⚠️</div>
					<div>
						<p className="text-sm font-semibold text-amber-800">Aún no has creado ningún usuario</p>
						<p className="text-sm text-amber-700 mt-1">
							Como Contador, debes crear al menos un usuario (<strong>Cliente</strong> o <strong>Gestor</strong>) para comenzar a gestionarlo.
							Usa el botón <strong>"+ Crear Usuario"</strong> para empezar.
						</p>
					</div>
				</div>
			) : error ? (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			) : (
					<div className="bg-white rounded-lg shadow overflow-hidden">
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Usuario
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Email
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Rol
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Cédula
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											RNC Empresa
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Empresa
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Estado
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Fecha Registro
										</th>
										<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
											Acciones
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{filteredUsers.length === 0 ? (
										<tr>
											<td colSpan="9" className="px-6 py-8 text-center text-gray-500">
												No se encontraron usuarios
											</td>
										</tr>
									) : (
										filteredUsers.map((user) => (
											<tr key={user.id} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center">
														<UserCircleIcon className="h-8 w-8 text-gray-400 mr-3" />
														<div>
															<div className="text-sm font-medium text-gray-900">
																{user.first_name && user.last_name
																	? `${user.first_name} ${user.last_name}`
																	: user.username}
															</div>
															<div className="text-sm text-gray-500">{user.username}</div>
														</div>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-900">{user.email || '-'}</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.profile?.role || 'cliente')}`}>
														{getRoleLabel(user.profile?.role || 'cliente')}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-900">
														{user.profile?.cedula ? maskCedula(user.profile.cedula) : '-'}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-900">
														{user.profile?.rnc_empresa ? maskRNC(user.profile.rnc_empresa) : '-'}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-900">
														{user.profile?.empresa || '-'}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													{user.is_active && user.profile?.activo ? (
														<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
															Activo
														</span>
													) : (
														<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
															Inactivo
														</span>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{new Date(user.date_joined).toLocaleDateString('es-DO')}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<div className="flex items-center justify-end gap-2">
														<button
															onClick={() => handleEdit(user)}
															className="text-primary hover:text-primary/80"
															title="Editar"
														>
															<PencilIcon className="h-5 w-5" />
														</button>
														<button
															onClick={() => handleToggleActive(user.id)}
															className={user.is_active && user.profile?.activo 
																? "text-red-600 hover:text-red-800" 
																: "text-green-600 hover:text-green-800"}
															title={user.is_active && user.profile?.activo ? "Desactivar" : "Activar"}
														>
															{user.is_active && user.profile?.activo ? (
																<XMarkIcon className="h-5 w-5" />
															) : (
																<CheckIcon className="h-5 w-5" />
															)}
														</button>
													</div>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{/* Modal Crear/Editar Usuario */}
				{(showCreateModal || showEditModal) && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
						<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
							<div className="p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-2xl font-bold text-gray-900">
										{editingUser ? 'Editar Usuario' : 'Crear Usuario'}
									</h2>
									<button
										onClick={() => {
											setShowCreateModal(false);
											setShowEditModal(false);
											setEditingUser(null);
											setFormErrors({});
										}}
										className="text-gray-400 hover:text-gray-600"
									>
										<XMarkIcon className="h-6 w-6" />
									</button>
								</div>

								<form onSubmit={handleSave} className="space-y-4">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Nombre
											</label>
											<input
												type="text"
												value={formData.first_name}
												onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Apellido
											</label>
											<input
												type="text"
												value={formData.last_name}
												onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Usuario *
											</label>
											<input
												type="text"
												required
												value={formData.username}
												onChange={(e) => setFormData({ ...formData, username: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
											{formErrors.username && (
												<p className="mt-1 text-sm text-red-600">{formErrors.username[0]}</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Email *
											</label>
											<input
												type="email"
												required
												value={formData.email}
												onChange={(e) => setFormData({ ...formData, email: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
											{formErrors.email && (
												<p className="mt-1 text-sm text-red-600">{formErrors.email[0]}</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												{editingUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
											</label>
											<input
												type="password"
												required={!editingUser}
												minLength={8}
												value={formData.password}
												onChange={(e) => setFormData({ ...formData, password: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
											{formErrors.password && (
												<p className="mt-1 text-sm text-red-600">{formErrors.password[0]}</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Confirmar Contraseña {!editingUser && '*'}
											</label>
											<input
												type="password"
												required={!editingUser}
												minLength={8}
												value={formData.password_confirm}
												onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
											{formErrors.password_confirm && (
												<p className="mt-1 text-sm text-red-600">{formErrors.password_confirm}</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Teléfono
											</label>
											<input
												type="tel"
												value={formData.telefono}
												onChange={(e) => {
													const masked = maskPhone(e.target.value);
													setFormData({ ...formData, telefono: masked });
												}}
												placeholder="(809) 123-4567"
												maxLength={14}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Cédula
											</label>
											<input
												type="text"
												value={formData.cedula}
												onChange={(e) => {
													const masked = maskCedula(e.target.value);
													setFormData({ ...formData, cedula: masked });
												}}
												placeholder="001-1234567-8"
												maxLength={13}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												RNC de la Empresa
											</label>
											<input
												type="text"
												value={formData.rnc_empresa}
												onChange={(e) => {
													// Solo permitir números, sin máscara
													const numbers = e.target.value.replace(/\D/g, '');
													setFormData({ ...formData, rnc_empresa: numbers });
												}}
												placeholder="123456789 o 12345678901"
												maxLength={11}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Empresa
											</label>
											<input
												type="text"
												value={formData.empresa}
												onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Actividad Económica
											</label>
									<ActividadEconomicaSelector
											value={formData.actividad_economica}
											onChange={(actividad) => {
												if (actividad) {
													setFormData({ 
														...formData, 
														actividad_economica: actividad.codigo + ' - ' + actividad.descripcion 
													});
												} else {
													setFormData({ ...formData, actividad_economica: '' });
												}
											}}
											onAddNew={(searchTerm) => {
												setNuevaActividadData({ codigo: '', descripcion: searchTerm, literal_grupo: '', descripcion_grupo: '' });
												setShowActividadModal(true);
											}}
											showGrupos={false}
											placeholder="Buscar actividad económica..."
										/>
										</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Rol *
										</label>
										<select
											required
											value={formData.role}
											onChange={(e) => setFormData({ ...formData, role: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
										>
											{rolesDisponibles.map(r => (
												<option key={r.value} value={r.value}>{r.label}</option>
											))}
										</select>
									</div>

										{editingUser && (
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Estado
												</label>
												<select
													value={formData.is_active}
													onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
												>
													<option value={true}>Activo</option>
													<option value={false}>Inactivo</option>
												</select>
											</div>
										)}
									</div>

									<div className="flex justify-end gap-3 pt-4 border-t">
										<button
											type="button"
											onClick={() => {
												setShowCreateModal(false);
												setShowEditModal(false);
												setEditingUser(null);
												setFormErrors({});
											}}
											className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
										>
											Cancelar
										</button>
										<button
											type="submit"
											disabled={saving}
											className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
										>
											{saving ? 'Guardando...' : 'Guardar'}
										</button>
									</div>
								</form>
							</div>
						</div>
					</div>
			)}

			{/* Modal para crear nueva actividad económica */}
			{showActividadModal && (
				<div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
						<div className="p-6">
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-lg font-bold text-gray-900">Crear Nueva Actividad Económica</h3>
								<button
									onClick={() => {
										setShowActividadModal(false);
										setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
									}}
									className="text-gray-400 hover:text-gray-600"
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Código <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										value={nuevaActividadData.codigo}
										onChange={(e) => {
											const value = e.target.value.replace(/\D/g, '').slice(0, 6);
											setNuevaActividadData({ ...nuevaActividadData, codigo: value });
										}}
										placeholder="0001 o 151123 (máximo 6 dígitos)"
										maxLength={6}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Descripción <span className="text-red-500">*</span>
									</label>
									<textarea
										value={nuevaActividadData.descripcion}
										onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, descripcion: e.target.value })}
										placeholder="Descripción de la actividad..."
										rows={3}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Literal Grupo
										</label>
										<input
											type="text"
											value={nuevaActividadData.literal_grupo}
											onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, literal_grupo: e.target.value.toUpperCase() })}
											placeholder="A-Q (Opcional)"
											maxLength={1}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Descripción Grupo
										</label>
										<input
											type="text"
											value={nuevaActividadData.descripcion_grupo}
											onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, descripcion_grupo: e.target.value })}
											placeholder="Opcional"
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
										/>
									</div>
								</div>
								<div className="flex gap-3 pt-4">
									<button
										type="button"
										onClick={() => {
											setShowActividadModal(false);
											setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
										}}
										className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="button"
										onClick={async () => {
											if (!nuevaActividadData.codigo || !nuevaActividadData.descripcion) {
												alert('Código y descripción son requeridos');
												return;
											}
											try {
												await dgiiApi.createActividadEconomica({
													...nuevaActividadData,
													activa: true
												});
												setFormData({
													...formData,
													actividad_economica: nuevaActividadData.codigo + ' - ' + nuevaActividadData.descripcion
												});
												setShowActividadModal(false);
												setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
											} catch (err) {
												console.error('Error creando actividad:', err);
												alert('Error: ' + (err.response?.data?.detail || err.message));
											}
										}}
										className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
									>
										Crear
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
	</div>
	);
};

export default UserManagement;

